import {
  format,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  startOfDay,
  endOfDay,
  subDays,
  subWeeks,
  subMonths,
} from "date-fns";
import { z } from "zod";
import { useCalendarDefaults, type Omittable } from "@salestrak/ui";

export type DatePresetKey =
  | "all-time"
  | "today"
  | "yesterday"
  | "this-week"
  | "last-week"
  | "mtd"
  | "last-month"
  | "ytd"
  | "custom";

export type DatePresetCustom = { startDate: string; endDate: string };

export type DatePresetValue = Omittable<{
  key: DatePresetKey;
  custom?: DatePresetCustom;
}>;

/**
 * Zod mirrors of the preset types, so consumers that persist a date preset in a
 * schema-validated config (widget cards; the forms domain later) validate it instead
 * of re-declaring the shape. The `z.enum` literals MUST stay in sync with
 * {@link DatePresetKey} — the `satisfies` check below fails the build if they drift.
 */
export const datePresetKeySchema = z.enum([
  "all-time",
  "today",
  "yesterday",
  "this-week",
  "last-week",
  "mtd",
  "last-month",
  "ytd",
  "custom",
]);
// Compile-time guard: the enum's inferred type must equal the hand-written union.
type _DatePresetKeyInSync = z.infer<typeof datePresetKeySchema> extends DatePresetKey
  ? DatePresetKey extends z.infer<typeof datePresetKeySchema>
    ? true
    : never
  : never;
const _datePresetKeyInSync: _DatePresetKeyInSync = true;
void _datePresetKeyInSync;

export const datePresetValueSchema = z.object({
  key: datePresetKeySchema,
  custom: z.object({ startDate: z.string(), endDate: z.string() }).optional(),
});

export type ResolvedDateRange = { startDate: string; endDate: string };

export type DatePresetOption = {
  key: DatePresetKey;
  label: string;
  shortLabel: string;
  /** Quiet second line in the picker, for a preset whose label alone doesn't
   *  say what picking it means ("All time" is the absence of a date filter,
   *  not a window). */
  description?: string;
};

export const DATE_PRESETS: ReadonlyArray<DatePresetOption> = [
  {
    key: "all-time",
    label: "All Time",
    shortLabel: "All Time",
    description: "No date limit",
  },
  { key: "today", label: "Today", shortLabel: "Today" },
  { key: "yesterday", label: "Yesterday", shortLabel: "Yesterday" },
  { key: "this-week", label: "This Week", shortLabel: "This Week" },
  { key: "last-week", label: "Last Week", shortLabel: "Last Week" },
  { key: "mtd", label: "Month To Date", shortLabel: "MTD" },
  { key: "last-month", label: "Last Month", shortLabel: "Last Month" },
  { key: "ytd", label: "Year To Date", shortLabel: "YTD" },
  { key: "custom", label: "Custom Date Range", shortLabel: "Custom" },
];

export const DEFAULT_PRESET: DatePresetKey = "this-week";

type WeekStartsOn = 0 | 1 | 2 | 3 | 4 | 5 | 6;

// The viewer's "First day of the week" preference. `AppCalendarProvider`
// publishes it here during render (as it does the clock for `format-date`),
// because the presets also resolve outside React (`build-where`). Sunday until
// the preference loads.
let weekStartsOn: WeekStartsOn = 0;

export function setWeekStartsOn(next: WeekStartsOn): void {
  weekStartsOn = next;
}

export function getWeekStartsOn(): WeekStartsOn {
  return weekStartsOn;
}

/**
 * The week start as a render value, for the dependency list of a memo that
 * resolves a preset. The module value above changes without re-rendering
 * anyone, so a memo keyed only on the preset would keep the old week.
 */
export function useWeekStartsOn(): WeekStartsOn {
  return useCalendarDefaults().weekStartsOn ?? weekStartsOn;
}

export function resolveDatePreset(
  key: DatePresetKey,
  custom?: DatePresetCustom,
  now: Date = new Date()
): ResolvedDateRange {
  const WEEK_OPTS = { weekStartsOn };
  switch (key) {
    case "all-time": {
      // No lower bound — the widest window any consumer can resolve to. Widgets
      // driven by the dashboard skip the date clause entirely for this key (see
      // `useScopedWhere`); this concrete range is the fallback for consumers that
      // must resolve to literal dates (axis labels, "vs. last period" copy).
      return { startDate: new Date(0).toISOString(), endDate: endOfDay(now).toISOString() };
    }
    case "today": {
      return { startDate: startOfDay(now).toISOString(), endDate: endOfDay(now).toISOString() };
    }
    case "yesterday": {
      const y = subDays(now, 1);
      return { startDate: startOfDay(y).toISOString(), endDate: endOfDay(y).toISOString() };
    }
    case "this-week": {
      return {
        startDate: startOfWeek(now, WEEK_OPTS).toISOString(),
        endDate: endOfWeek(now, WEEK_OPTS).toISOString(),
      };
    }
    case "last-week": {
      const lastWeek = subWeeks(now, 1);
      return {
        startDate: startOfWeek(lastWeek, WEEK_OPTS).toISOString(),
        endDate: endOfWeek(lastWeek, WEEK_OPTS).toISOString(),
      };
    }
    case "mtd": {
      return {
        startDate: startOfMonth(now).toISOString(),
        endDate: endOfDay(now).toISOString(),
      };
    }
    case "last-month": {
      const lastMonth = subMonths(now, 1);
      return {
        startDate: startOfMonth(lastMonth).toISOString(),
        endDate: endOfMonth(lastMonth).toISOString(),
      };
    }
    case "ytd": {
      return {
        startDate: startOfYear(now).toISOString(),
        endDate: endOfDay(now).toISOString(),
      };
    }
    case "custom": {
      if (!custom) {
        // No custom range yet — fall back to default preset to keep queries stable.
        return resolveDatePreset(DEFAULT_PRESET, undefined, now);
      }
      const start = parseISODate(custom.startDate);
      const end = parseISODate(custom.endDate);
      if (!start || !end) return custom;
      return {
        startDate: startOfDay(start).toISOString(),
        endDate: endOfDay(end).toISOString(),
      };
    }
  }
}

export function getPresetShortLabel(key: DatePresetKey): string {
  return DATE_PRESETS.find((p) => p.key === key)?.shortLabel ?? "";
}

/**
 * The face a chip shows for a window: the preset's short label, or for a
 * custom range its dates ("Oct 1 – Oct 8"; a year shows when the range is
 * not all in the current one), since "Custom" alone says nothing about it.
 */
export function formatPresetValueLabel(
  value: DatePresetValue,
  shortLabel: string = getPresetShortLabel(value.key),
  now: Date = new Date(),
): string {
  if (value.key !== "custom" || !value.custom) return shortLabel;
  const start = parseISODate(value.custom.startDate);
  const end = parseISODate(value.custom.endDate);
  if (!start || !end) return shortLabel;
  const year = now.getFullYear();
  const fmt =
    start.getFullYear() === year && end.getFullYear() === year ? "MMM d" : "MMM d, yyyy";
  const from = format(start, fmt);
  const to = format(end, fmt);
  return from === to ? from : `${from} – ${to}`;
}

export function isPresetValue(value: unknown): value is DatePresetValue {
  return (
    typeof value === "object" &&
    value !== null &&
    "key" in value &&
    typeof (value as { key: unknown }).key === "string"
  );
}

export function parseISODate(s: string | undefined | null): Date | null {
  if (!s) return null;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}
